<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="exportToExcel" :disabled="loading">
          <i class="ri-file-excel-line"></i>
          匯出 Excel
        </button>
        <button class="btn btn-secondary" @click="exportToCSV" :disabled="loading">
          <i class="ri-file-text-line"></i>
          匯出 CSV
        </button>
      </div>
    </div>

    <div class="page-stats">
      <div class="stat-card">
        <div class="stat-label">總客戶數</div>
        <div class="stat-value">{{ total }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">總應收金額</div>
        <div class="stat-value">NT$ {{ formatCurrency(totalAmount) }}</div>
      </div>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋客戶名稱或客戶代碼..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-box" v-if="isSystemAccount">
        <label>平台篩選：</label>
        <select v-model="selectedPlatformId" @change="handleFilterChange" class="form-control">
          <option value="">全部平台</option>
          <option
            v-for="platform in platforms"
            :key="platform.id"
            :value="platform.id"
          >
            {{ platform.name }} ({{ platform.code }})
          </option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchClients">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th v-if="isSystemAccount">平台</th>
            <th>客戶名稱 / 代碼</th>
            <th>方案名稱</th>
            <th>到期日</th>
            <th>寬限期結束日</th>
            <th>寬限期剩餘天數</th>
            <th>應收費用</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="clients.length === 0">
            <td :colspan="isSystemAccount ? 8 : 7" class="empty-state">
              沒有找到寬限期內的客戶
            </td>
          </tr>
          <template v-for="client in clients" :key="client.client_id">
            <tr>
              <td v-if="isSystemAccount">{{ client.platform_name || client.platform_code || '-' }}</td>
              <td>
                <span class="client-name">{{ client.client_name }}</span> / {{ client.client_code }}
              </td>
              <td>{{ client.plan_name }}</td>
              <td>{{ formatDate(client.subscription_end_date) }}</td>
              <td>{{ formatDate(client.grace_period_end_date) }}</td>
              <td>
                <span :class="getDaysRemainingClass(client.days_remaining_in_grace_period)">
                  {{ client.days_remaining_in_grace_period }} 天
                </span>
              </td>
              <td>
                <span class="amount">NT$ {{ formatCurrency(client.feeBreakdown?.totalAmount || 0) }}</span>
                <button
                  class="btn-link"
                  @click="toggleFeeDetail(client.client_id)"
                  v-if="client.feeBreakdown"
                >
                  <i :class="expandedClients.has(client.client_id) ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                </button>
              </td>
              <td>
                <div class="action-buttons">
                  <button
                    class="btn btn-sm btn-primary"
                    @click="handleRecordPayment(client)"
                    title="開啟訂閱詳情並記錄／延長收款"
                  >
                    <i class="ri-money-dollar-circle-line"></i>
                    記錄收款
                  </button>
                  <button
                    class="btn btn-sm btn-secondary"
                    @click="handleViewClient(client)"
                    title="編輯客戶資料"
                  >
                    <i class="ri-eye-line"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="expandedClients.has(client.client_id) && client.feeBreakdown">
              <td :colspan="isSystemAccount ? 8 : 7" class="fee-detail">
                <div class="fee-breakdown">
                  <div class="fee-item">
                    <span class="fee-label">方案費用：</span>
                    <span class="fee-value">NT$ {{ formatCurrency(client.feeBreakdown.planAmount || 0) }}</span>
                  </div>
                  <div class="fee-item">
                    <span class="fee-label">選購模組費用：</span>
                    <span class="fee-value">NT$ {{ formatCurrency(client.feeBreakdown.optionalModulesAmount || 0) }}</span>
                  </div>
                  <div v-if="client.feeBreakdown.optionalModulesDetail && client.feeBreakdown.optionalModulesDetail.length > 0" class="optional-modules">
                    <div class="modules-title">選購模組明細：</div>
                    <div
                      v-for="module in client.feeBreakdown.optionalModulesDetail"
                      :key="module.id || module.module_id || module.module_code"
                      class="module-item"
                    >
                      <div class="module-item-main">
                        <span class="module-name">{{ module.module_name || module.moduleName || '-' }}</span>
                        <span v-if="module.module_code || module.moduleCode" class="module-code">
                          {{ module.module_code || module.moduleCode }}
                        </span>
                        <span class="module-price">NT$ {{ formatCurrency(module.price || 0) }}</span>
                      </div>
                      <div class="module-item-meta">
                        <span>{{ formatBillingCycle(module.billing_cycle || module.billingCycle) }}</span>
                        <span v-if="getModuleSeats(module)">
                          座席：內含 {{ getModuleSeats(module).includedSeats }} ＋ 加購 {{ getModuleSeats(module).extraSeats }}
                          ＝ 共 {{ getModuleSeats(module).totalSeats }} 席
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />

    <SubscriptionDetailModal
      v-if="showSubscriptionModal && selectedClientId"
      :client-id="selectedClientId"
      @close="handleCloseSubscriptionModal"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { manualPaymentService } from '@/api/modules/manualPayments.js';
import { platformManagementService } from '@/api/modules/platforms.js';
import authService from '@/helpers/authservice/authService.js';
import { showError } from '@/utils/swal.js';
import SubscriptionDetailModal from '@/views/system/subscription-clients/Detail.vue';

export default {
  name: 'ManualPaymentGracePeriod',
  components: {
    SubscriptionDetailModal,
  },
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('寬限期款項提醒');
    const clients = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const totalAmount = ref(0);
    const searchTimeout = ref(null);
    const expandedClients = ref(new Set());
    const selectedPlatformId = ref('');
    const showSubscriptionModal = ref(false);
    const selectedClientId = ref(null);
    const platforms = ref([]);
    const isSystemAccount = computed(() => {
      return authService.getAccountType() === 'system';
    });

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const fetchPlatforms = async () => {
      if (!isSystemAccount.value) return;
      
      try {
        const response = await platformManagementService.getPlatforms({ page: 1, pageSize: 100 });
        if (response && response.success && response.data && response.data.items) {
          platforms.value = response.data.items.map(platform => ({
            id: platform.id,
            name: platform.platformName,
            code: platform.platformCode,
          }));
        }
      } catch (err) {
        console.error('獲取平台列表失敗:', err);
      }
    };

    const fetchClients = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        // 系統帳號可以選擇平台
        if (isSystemAccount.value && selectedPlatformId.value) {
          params.platformId = selectedPlatformId.value;
        }

        const response = await manualPaymentService.getGracePeriodClients(params);

        if (response && response.success && response.data) {
          clients.value = response.data.items || [];
          total.value = response.data.total || 0;
          totalAmount.value = response.data.totalAmount || 0;
        } else {
          error.value = (response && response.message) || '獲取客戶列表失敗';
        }
      } catch (err) {
        console.error('獲取客戶列表失敗:', err);
        error.value = err.message || '獲取客戶列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchClients();
      }, 500);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchClients();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchClients();
      }
    };

    const toggleFeeDetail = (clientId) => {
      if (expandedClients.value.has(clientId)) {
        expandedClients.value.delete(clientId);
      } else {
        expandedClients.value.add(clientId);
      }
    };

    const handleRecordPayment = (client) => {
      selectedClientId.value = client.client_id;
      showSubscriptionModal.value = true;
    };

    const handleCloseSubscriptionModal = () => {
      showSubscriptionModal.value = false;
      selectedClientId.value = null;
      fetchClients();
    };

    const handleViewClient = (client) => {
      router.push({ name: 'EditClient', params: { id: client.client_id } });
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW');
    };

    const formatCurrency = (amount) => {
      if (!amount) return '0';
      return new Intl.NumberFormat('zh-TW').format(amount);
    };

    const formatBillingCycle = (cycle) => {
      if (cycle === 'yearly') return '年繳';
      return '月繳';
    };

    const getModuleSeats = (module) => {
      if (!module) return null;
      const code = module.module_code || module.moduleCode;
      if (code !== 'CHATDESK') return null;
      return module.seats || null;
    };

    const getDaysRemainingClass = (days) => {
      if (days <= 3) return 'badge badge-danger';
      if (days <= 7) return 'badge badge-warning';
      return 'badge badge-info';
    };

    const exportToExcel = async () => {
      await showError('功能開發中', 'Excel 匯出功能開發中');
    };

    const exportToCSV = async () => {
      await showError('功能開發中', 'CSV 匯出功能開發中');
    };

    onMounted(async () => {
      if (isSystemAccount.value) {
        await fetchPlatforms();
      }
      fetchClients();
    });

    return {
      clients,
      loading,
      error,
      searchQuery,
      currentPage,
      totalPages,
      total,
      totalAmount,
      expandedClients,
      selectedPlatformId,
      platforms,
      showSubscriptionModal,
      selectedClientId,
      isSystemAccount,
      pageTitle,
      handleSearch,
      handleFilterChange,
      goToPage,
      toggleFeeDetail,
      handleRecordPayment,
      handleCloseSubscriptionModal,
      handleViewClient,
      formatDate,
      formatCurrency,
      formatBillingCycle,
      getModuleSeats,
      getDaysRemainingClass,
      exportToExcel,
      exportToCSV,
      fetchClients,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';

/* 頁面特定樣式覆寫 - 移除，使用共用樣式 */

.page-stats {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
}

.page-toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  max-width: 400px;
}

.filter-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.filter-box label {
  white-space: nowrap;
  font-size: 0.875rem;
  color: #666;
  min-width: fit-content;
}

.filter-box select {
  width: 200px;
  min-width: 150px;
  max-width: 250px;
}

.fee-detail {
  background-color: #f8f9fa;
  padding: 1rem;
}

.fee-breakdown {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.fee-item {
  display: flex;
  justify-content: space-between;
}

.fee-label {
  font-weight: 500;
}

.fee-value {
  color: #007bff;
  font-weight: bold;
}

.optional-modules {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #dee2e6;
}

.modules-title {
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.module-item {
  padding: 0.5rem 0 0.5rem 1rem;
  color: #666;
  font-size: 0.875rem;
  border-bottom: 1px solid #eee;
}

.module-item:last-child {
  border-bottom: none;
}

.module-item-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.module-name {
  font-weight: 600;
  color: #333;
}

.module-code {
  font-size: 0.75rem;
  color: #6b7280;
  background: #eef2ff;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
}

.module-price {
  margin-left: auto;
  font-weight: 600;
  color: #007bff;
}

.module-item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.25rem;
  font-size: 0.8125rem;
  color: #888;
}

.amount {
  font-weight: bold;
  color: #007bff;
}

.btn-link {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  padding: 0.25rem;
  margin-left: 0.5rem;
}

.btn-link:hover {
  color: #0056b3;
}

.client-name {
  color: #2563eb;
  font-weight: 500;
}
</style>
